import { useState, useEffect, useRef } from "react";
import {
    Box, TextField, IconButton, Tooltip, InputAdornment, Typography
} from "@mui/material";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import CancelIcon from "@mui/icons-material/Cancel";
import { Html5Qrcode } from "html5-qrcode";

// ── Toggle camera on/off here ─────────────────────────
const CAMERA_ENABLED = true;
// ─────────────────────────────────────────────────────

const BarcodeScanner = ({ onScan, disabled = false }) => {

    const [barcode, setBarcode]         = useState("");
    const [cameraActive, setCameraActive] = useState(false);
    const [cameraError, setCameraError] = useState("");
    const inputRef                      = useRef(null);
    const scannerRef                    = useRef(null);
    const cameraId                      = "barcode-camera-feed";

    // ── Always keep input focused (USB/Bluetooth) ──────
    useEffect(() => {
        if (!cameraActive && !disabled) {
            inputRef.current?.focus();
        }
    }, [cameraActive, disabled]);

    // Refocus if user clicks elsewhere on the page
    useEffect(() => {
        const refocus = () => {
            if (!cameraActive && !disabled) {
                inputRef.current?.focus();
            }
        };
        document.addEventListener("click", refocus);
        return () => document.removeEventListener("click", refocus);
    }, [cameraActive, disabled]);

    // ── Cleanup camera on unmount ──────────────────────
    useEffect(() => {
        return () => {
            stopCamera();
        };
    }, []);

    // ── Handle USB/Bluetooth Enter key ─────────────────
    const handleKeyDown = (e) => {
        if (e.key === "Enter" && barcode.trim()) {
            onScan(barcode.trim());
            setBarcode("");
        }
    };

    // ── Camera: Start ──────────────────────────────────
    const startCamera = async () => {
        setCameraError("");
        setCameraActive(true);
        try {
            const html5Qrcode = new Html5Qrcode(cameraId);
            scannerRef.current = html5Qrcode;
            await html5Qrcode.start(
                { facingMode: "environment" }, // rear camera on phones
                { fps: 10, qrbox: { width: 250, height: 150 } },
                (decodedText) => {
                    onScan(decodedText);
                    stopCamera();
                },
                () => {} // ignore per-frame errors
            );
        } catch (err) {
            setCameraError("Camera not available or permission denied.");
            setCameraActive(false);
        }
    };

    // ── Camera: Stop ───────────────────────────────────
    const stopCamera = async () => {
        try {
            if (scannerRef.current) {
                await scannerRef.current.stop();
                scannerRef.current = null;
            }
        } catch (_) {}
        setCameraActive(false);
    };

    return (
        <Box display="flex" flexDirection="column" gap={1}>

            {/* Barcode Text Input */}
            <TextField
                inputRef={inputRef}
                label="Scan or enter barcode"
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                onKeyDown={handleKeyDown}
                size="small"
                fullWidth
                disabled={disabled || cameraActive}
                placeholder="Scan with device or type + Enter"
                InputProps={{
                    endAdornment: CAMERA_ENABLED && (
                        <InputAdornment position="end">
                            <Tooltip title={cameraActive ? "Close camera" : "Use camera"}>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={cameraActive ? stopCamera : startCamera}
                                        disabled={disabled}
                                    >
                                        {cameraActive
                                            ? <CancelIcon fontSize="small" color="error" />
                                            : <QrCodeScannerIcon fontSize="small" />
                                        }
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </InputAdornment>
                    )
                }}
            />

            {/* Camera Feed */}
            {cameraActive && (
                <Box
                    id={cameraId}
                    borderRadius={1}
                    overflow="hidden"
                    border="1px solid"
                    borderColor="divider"
                    width="100%"
                />
            )}

            {/* Camera Error */}
            {cameraError && (
                <Typography variant="caption" color="error">
                    {cameraError}
                </Typography>
            )}

            {/* Hint */}
            {!cameraActive && (
                <Typography variant="caption" color="text.secondary">
                    USB/Bluetooth scanner ready — or type barcode and press Enter
                </Typography>
            )}
        </Box>
    );
};

export default BarcodeScanner;